import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { OrganizationSwitcher } from "@/components/pm/OrganizationSwitcher";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import {
  Building2, Clock, FileText, CreditCard, LayoutDashboard, LogOut, Plus, Settings, Users, Scale, Menu, FolderKanban
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";

import bricosLogo from "@/assets/bricos-logo.png";

interface PMSidebarProps {
  active: "dashboard" | "properties" | "jobs" | "reports" | "billing" | "book" | "settings" | "team" | "contractors" | "disputes" | "projects";
}

const PMSidebar = ({ active }: PMSidebarProps) => {
  const { signOut } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { can } = useOrgPermissions();

  const pm = t.pm || {} as any;

  const allLinks = [
    { key: "dashboard", label: pm.dashboard || "Dashboard", icon: LayoutDashboard, href: "/pm/dashboard", permission: "view_dashboard" as const },
    { key: "properties", label: pm.properties || "Properties", icon: Building2, href: "/pm/properties", permission: "view_properties" as const },
    { key: "contractors", label: pm.contractors || "Contractors", icon: Users, href: "/pm/contractors", permission: "view_contractors" as const },
    { key: "jobs", label: pm.jobs || "Jobs", icon: Clock, href: "/pm/jobs", permission: "view_jobs" as const },
    { key: "book", label: pm.bookService || "Book Service", icon: Plus, href: "/pm/book", permission: "book_service" as const },
    { key: "disputes", label: pm.disputes || "Disputes", icon: Scale, href: "/pm/disputes", permission: "view_disputes" as const },
    { key: "reports", label: pm.reports || "Reports", icon: FileText, href: "/pm/reports", permission: "view_reports" as const },
    { key: "billing", label: pm.billing || "Billing", icon: CreditCard, href: "/pm/billing", permission: "view_billing" as const },
    { key: "projects", label: pm.projects || "Projects", icon: FolderKanban, href: "/projects", permission: "view_jobs" as const },
  ];

  const allAdminLinks = [
    { key: "team", label: pm.team || "Team", icon: Users, href: "/pm/settings?tab=team", permission: "manage_team" as const },
    { key: "settings", label: pm.settings || "Settings", icon: Settings, href: "/pm/settings", permission: "view_settings" as const },
  ];

  const links = allLinks.filter(link => can(link.permission));
  const adminLinks = allAdminLinks.filter(link => can(link.permission));

  const SidebarContent = ({ onLinkClick }: { onLinkClick?: () => void }) => (
    <>
      {/* Logo */}
      <Link to="/" className="flex items-center gap-3 mb-4" onClick={onLinkClick}>
        <div className="relative">
          <img 
            src={bricosLogo} 
            alt="Bricos" 
            className="h-10 w-10 rounded-xl object-contain bg-white/10 p-1"
          />
          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-blue-400 rounded-full flex items-center justify-center">
            <Building2 className="w-2.5 h-2.5 text-blue-900" />
          </div>
        </div>
        <div>
          <span className="font-display text-lg font-bold block text-white">Bricos</span>
          <span className="text-xs text-blue-300">{pm.portal || "Property Manager"}</span>
        </div>
      </Link>

      {/* Organization Switcher */}
      <OrganizationSwitcher className="mb-4 -mx-1" />

      <Separator className="bg-blue-700/50 mb-4" />

      <nav className="space-y-1 flex-1 overflow-y-auto min-h-0">
        {links.map((link) => (
          <Link
            key={link.key}
            to={link.href}
            onClick={onLinkClick}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
              active === link.key
                ? "bg-white/20 text-white font-medium backdrop-blur-sm"
                : "text-blue-200 hover:bg-white/10 hover:text-white"
            )}
          >
            <link.icon className="w-5 h-5" />
            {link.label}
          </Link>
        ))}

        <Separator className="bg-blue-700/50 my-3" />

        {adminLinks.map((link) => (
          <Link
            key={link.key}
            to={link.href}
            onClick={onLinkClick}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
              active === link.key
                ? "bg-white/20 text-white font-medium backdrop-blur-sm"
                : "text-blue-200 hover:bg-white/10 hover:text-white"
            )}
          >
            <link.icon className="w-5 h-5" />
            {link.label}
          </Link>
        ))}
      </nav>

      <div className="pt-4 border-t border-blue-700/50 space-y-1">
        <LanguageSwitcher variant="sidebar-dark" />
        <ThemeToggle variant="sidebar-dark" />
        <Button 
          variant="ghost" 
          className="w-full justify-start text-blue-200 hover:text-white hover:bg-white/10" 
          onClick={async () => {
            await signOut();
            navigate("/");
          }}
        >
          <LogOut className="w-4 h-4 mr-2" />
          {pm.signOut || "Sign Out"}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-800 to-blue-900 border-b border-blue-700/50">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={bricosLogo} 
              alt="Bricos" 
              className="h-8 w-8 rounded-lg object-contain bg-white/10 p-0.5"
            />
            <span className="font-display text-lg font-bold text-white">Bricos</span>
          </Link>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-4 bg-gradient-to-b from-blue-800 to-blue-900 border-blue-700/50">
              <div className="flex flex-col h-full">
                <SidebarContent onLinkClick={() => setMobileOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Mobile spacer */}
      <div className="lg:hidden h-14" />

      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-gradient-to-b from-blue-800 to-blue-900 p-4 hidden lg:flex flex-col z-40">
        <SidebarContent />
      </aside>
    </>
  );
};

export default PMSidebar;
